import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { identifyUser as posthogIdentify, resetUser as posthogReset } from '@/lib/posthog';
export type UserAccessLevel = 'visitor' | 'logged_unsubscribed' | 'subscribed';

export interface UserAccessState {
  user: User | null;
  accessLevel: UserAccessLevel;
  isLoading: boolean;
  canAccessPremium: boolean;
  canCreateListings: boolean;
  canViewContact: boolean;
  subscriptionEnd: string | null;
  subscriptionTier: string | null;
  hasListings: boolean;
  cancelAtPeriodEnd: boolean;
  cancelAt: string | null;
  refreshAccess: (opts?: { force?: boolean }) => Promise<void>;
}

const UserAccessContext = createContext<UserAccessState | undefined>(undefined);

export const UserAccessProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [accessLevel, setAccessLevel] = useState<UserAccessLevel>('visitor');
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [subscriptionTier, setSubscriptionTier] = useState<string | null>(null);
  const [hasListings, setHasListings] = useState(false);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [cancelAt, setCancelAt] = useState<string | null>(null);
  const backgroundCheckRunning = useRef(false);
  const lastBackgroundCheck = useRef<number>(0);
  const welcomeEmailSent = useRef(false); // Prevent multiple welcome emails
  const hadUserRef = useRef(false); // Tracks logout transitions for PostHog reset

  const refreshAccess = useCallback(async (opts?: { force?: boolean }) => {
    try {
      setIsLoading(true);
      const { data } = await supabase.auth.getSession();
      const currentUser = data.session?.user || null;
      setUser(currentUser);

      if (!currentUser) {
        setAccessLevel('visitor');
        setSubscriptionEnd(null);
        setSubscriptionTier(null);
        setHasListings(false);
        // Reset PostHog identity only on an actual logout transition. Calling
        // reset() on every anonymous page load would mint a new distinct_id
        // each time and fragment the visitor's session in analytics.
        if (hadUserRef.current) posthogReset();
        hadUserRef.current = false;
        return;
      }
      hadUserRef.current = true;

      // Check subscription from DB
      const { data: subData } = await supabase.rpc('is_user_subscribed', {
        _uid: currentUser.id,
      });

      // Identify user in PostHog with email
      if (currentUser.email) {
        posthogIdentify(currentUser.id, currentUser.email, {
          subscription_status: subData ? 'subscribed' : 'unsubscribed',
          created_at: currentUser.created_at,
        });
      }

      // If forced, run backend Stripe sync first to get freshest cancellation state
      if (opts?.force) {
        try {
          await supabase.functions.invoke('check-subscription', {
            body: {},
            // pass ?force=1 via headers; client appends query through path
          } as any);
          // The invoke helper doesn't easily allow query strings; do a direct fetch instead
          const { data: sessionData } = await supabase.auth.getSession();
          const accessToken = sessionData.session?.access_token;
          if (accessToken) {
            const fnUrl = `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID}.supabase.co/functions/v1/check-subscription?force=1`;
            await fetch(fnUrl, {
              method: 'POST',
              headers: { Authorization: `Bearer ${accessToken}` },
            }).catch(() => {});
          }
        } catch (e) {
          console.warn('Force check-subscription failed (non-fatal):', e);
        }
      }

      // Get subscription details
      const { data: subscriberData } = await supabase
        .rpc('get_my_subscription')
        .maybeSingle();
      
      const subData2 = subscriberData as {
        subscription_tier: string | null;
        subscription_end: string | null;
        first_listing_at: string | null;
        cancel_at_period_end: boolean | null;
        cancel_at: string | null;
      } | null;
      
      const tier = subData2?.subscription_tier || null;
      setSubscriptionTier(tier);
      setSubscriptionEnd(subData2?.subscription_end || null);
      setCancelAtPeriodEnd(!!subData2?.cancel_at_period_end);
      setCancelAt(subData2?.cancel_at || null);
      
      // Check if user has any listings (for Pending status logic)
      const userHasListings = subData2?.first_listing_at != null;
      setHasListings(userHasListings);

      if (subData) {
        setAccessLevel('subscribed');
        
        // Send welcome email ONLY ONCE - at first login after subscription
        // Check localStorage flag - if set, never send again
        const welcomeStorageKey = `ram_welcome_email_sent_once:${currentUser.id}`;
        let alreadySent = false;
        try {
          alreadySent = localStorage.getItem(welcomeStorageKey) === 'true';
        } catch {
          // ignore storage errors
        }

        if (!welcomeEmailSent.current && !alreadySent && currentUser.email) {
          welcomeEmailSent.current = true;
          // Mark as sent permanently (never send again)
          try {
            localStorage.setItem(welcomeStorageKey, 'true');
          } catch {
            // ignore storage errors
          }

          setTimeout(() => {
            supabase.functions
              .invoke('send-welcome-back-email', {
                body: {
                  user_id: currentUser.id,
                  email: currentUser.email,
                },
              })
              .catch((err) => console.log('Welcome email error:', err));
          }, 5000);
        }
      } else {
        setAccessLevel('logged_unsubscribed');
        
        // Send onboarding email to NEW unsubscribed users (once per user)
        const onboardingStorageKey = `ram_onboarding_email_sent:${currentUser.id}`;
        let onboardingAlreadySent = false;
        try {
          onboardingAlreadySent = localStorage.getItem(onboardingStorageKey) === 'true';
        } catch {
          // ignore storage errors
        }

        if (!onboardingAlreadySent && currentUser.email) {
          try {
            localStorage.setItem(onboardingStorageKey, 'true');
          } catch {
            // ignore storage errors
          }

          setTimeout(() => {
            supabase.functions
              .invoke('send-onboarding-email', {
                body: {
                  user_id: currentUser.id,
                  email: currentUser.email,
                },
              })
              .catch((err) => console.log('Onboarding email error:', err));
          }, 3000);
        }
      }

      // Background Stripe sync (heavily throttled - only every 5 minutes)
      const now = Date.now();
      const shouldRunBackgroundCheck = !backgroundCheckRunning.current && (now - lastBackgroundCheck.current) > 300000;

      if (shouldRunBackgroundCheck) {
        backgroundCheckRunning.current = true;
        lastBackgroundCheck.current = now;
        
        // Delay background check to not interfere with page load
        setTimeout(async () => {
          try {
            await supabase.functions.invoke('check-subscription');
            // Only refresh if not already subscribed (avoid redundant calls)
            if (accessLevel !== 'subscribed') {
              const { data: latest } = await supabase.rpc('is_user_subscribed', { _uid: currentUser.id });
              if (latest) {
                setAccessLevel('subscribed');
                const { data: latestSub } = await supabase
                  .rpc('get_my_subscription')
                  .maybeSingle();
                setSubscriptionEnd(latestSub?.subscription_end || null);
              }
            }
          } catch (e) {
            console.error('Background sync error:', e);
          } finally {
            backgroundCheckRunning.current = false;
          }
        }, 10000); // 10 second delay
      }
    } catch (error) {
      console.error('Error checking user access:', error);
      setAccessLevel('logged_unsubscribed');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshAccess();
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      refreshAccess();
    });
    return () => subscription.unsubscribe();
  }, [refreshAccess]);

  useEffect(() => {
    if (!user?.id) return;
    
    let timeoutId: NodeJS.Timeout;
    const channel = supabase
      .channel(`subscribers-updates-${Math.random().toString(36).slice(2)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'subscribers', filter: `user_id=eq.${user.id}` }, () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => refreshAccess(), 3000);
      })
      .subscribe();
    return () => {
      clearTimeout(timeoutId);
      supabase.removeChannel(channel);
    };
  }, [user?.id, refreshAccess]);

  const canAccessPremium = accessLevel === 'subscribed';
  // All logged-in users can create listings:
  // - Visitor: false (not logged in)
  // - Everyone else: true (can at least try to create - guards on pages handle blocking if needed)
  const canCreateListings = accessLevel !== 'visitor';
  const canViewContact = accessLevel !== 'visitor';

  return (
    <UserAccessContext.Provider value={{
      user,
      accessLevel,
      isLoading,
      canAccessPremium,
      canCreateListings,
      canViewContact,
      subscriptionEnd,
      subscriptionTier,
      hasListings,
      cancelAtPeriodEnd,
      cancelAt,
      refreshAccess,
    }}>
      {children}
    </UserAccessContext.Provider>
  );
};

export const useUserAccess = (): UserAccessState => {
  const context = useContext(UserAccessContext);
  if (context === undefined) {
    throw new Error('useUserAccess must be used within a UserAccessProvider');
  }
  return context;
};
